import React, { useState, useEffect, useCallback, useRef } from 'react';
import leadsWebApi from '../services/leads-web';
import { isKnownCrm360Statut } from '../utils/crm360Funnel';
import './Crm360.css';

const fmt = (d) => d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '';
const MATCH_LABEL = { matched: '✅ Rapproché', unmatched: '❌ Non trouvé', ambiguous: '⚠️ Ambigu' };

export default function LeadsCrm360Import() {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [rows, setRows] = useState([]);
  const [imports, setImports] = useState([]);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try { const r = await leadsWebApi.getCrm360Imports(); setImports(r.data.imports || []); } catch {}
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const loadRows = useCallback(async (importId) => {
    try {
      const r = await leadsWebApi.getCrm360ImportRows(importId, 'unmatched,ambiguous');
      setRows(r.data.rows || []);
    } catch { setRows([]); }
  }, []);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true); setResult(null); setRows([]);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await leadsWebApi.importCrm360(fd);
      setResult(r.data);
      await loadRows(r.data.import_id);
      await loadHistory();
    } catch (e) {
      alert('Erreur : ' + (e.response?.data?.error || e.message));
    } finally { setUploading(false); }
  };

  return (
    <div>
      <p className="imp-hint" style={{ marginBottom: 12 }}>
        Importez l'export Excel de <strong>CRM360</strong> (Suivi des leads). Le statut commercial (colonne « Statut ») est
        rapproché de vos leads via le <strong>N° de lead interne</strong> (= N° CRM360 saisi par l'opérateur lors du passage en statut « CRM360 »),
        avec la concession (« Entité ») comme départage en cas de doublon. Les lignes non rapprochées sont listées pour correction, sans fausser le funnel.
      </p>

      <div
        className={`imp-drop${dragging ? ' drag' : ''}`}
        onClick={() => fileRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
      >
        <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
          onChange={e => { handleFile(e.target.files?.[0]); e.target.value = ''; }} />
        <div className="imp-drop-emoji">📥</div>
        <div style={{ fontWeight: 600, marginTop: 6 }}>{uploading ? 'Import en cours…' : 'Glisser-déposer le fichier CRM360 (.xlsx) ou cliquer'}</div>
      </div>

      {result && (
        <>
          <div className="imp-cards">
            <div className="imp-card ok"><div className="imp-card-val">{result.matched}</div><div className="imp-card-lbl">Rapprochés</div></div>
            <div className="imp-card ko"><div className="imp-card-val">{result.unmatched}</div><div className="imp-card-lbl">Non trouvés</div></div>
            <div className="imp-card amb"><div className="imp-card-val">{result.ambiguous}</div><div className="imp-card-lbl">Ambigus</div></div>
            <div className="imp-card"><div className="imp-card-val">{result.total}</div><div className="imp-card-lbl">Lignes total</div></div>
          </div>

          {result.statuts_rencontres?.length > 0 && (
            <>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: 4 }}>Statuts rencontrés :</div>
              <div className="imp-statuts">
                {result.statuts_rencontres.map(s => (
                  <span key={s.statut} className={`imp-statut-chip${isKnownCrm360Statut(s.statut) ? '' : ' unknown'}`} title={isKnownCrm360Statut(s.statut) ? '' : 'Statut non reconnu — à classer dans le funnel'}>
                    {s.statut} · {s.n}{isKnownCrm360Statut(s.statut) ? '' : ' ⚠️'}
                  </span>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {rows.length > 0 && (
        <div className="table-container" style={{ marginTop: 14 }}>
          <table>
            <thead><tr><th>N° interne</th><th>Nom</th><th>Entité</th><th>Statut CRM360</th><th>Résultat</th></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td>{r.lead_interne}</td>
                  <td>{r.nom || '—'}</td>
                  <td>{r.entite || '—'}</td>
                  <td>{r.statut_crm360 || '—'}</td>
                  <td>{MATCH_LABEL[r.match_status] || r.match_status}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="imp-hint" style={{ padding: '8px 12px' }}>
            Lignes non rapprochées / ambiguës : vérifiez le N° de lead CRM360 saisi sur le lead correspondant (onglet du service, lead archivé), puis réimportez.
          </p>
        </div>
      )}

      {imports.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <h4 style={{ fontSize: '0.95rem', color: 'var(--text-primary)', margin: '0 0 8px' }}>🕑 Historique des imports</h4>
          <div className="table-container">
            <table>
              <thead><tr><th>Date</th><th>Fichier</th><th>Période</th><th>Rapprochés</th><th>Non trouvés</th><th>Ambigus</th><th>Par</th></tr></thead>
              <tbody>
                {imports.map(im => (
                  <tr key={im.id} style={{ cursor: 'pointer' }} onClick={() => loadRows(im.id)} title="Voir les lignes non rapprochées">
                    <td>{fmt(im.created_at)}</td>
                    <td>{im.filename}</td>
                    <td>{im.periode_label || '—'}</td>
                    <td style={{ color: 'var(--color-success)', fontWeight: 600 }}>{im.matched}</td>
                    <td style={{ color: 'var(--color-error)' }}>{im.unmatched}</td>
                    <td style={{ color: 'var(--color-warning)' }}>{im.ambiguous}</td>
                    <td>{im.user_prenom} {im.user_nom}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
